<template>
  <div class="chatbot-wrapper">
    <!-- Floating Button -->
    <button 
      v-if="!isOpen"
      class="chatbot-floating-button"
      @click="openChat"
      aria-label="Open AI Chat"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill="currentColor" 
        class="chatbot-icon"
      >
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
        <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
      </svg>
    </button>

    <!-- Chat Window -->
    <transition name="chat-slide">
      <div 
        v-if="isOpen"
        :class="['chatbot-container', { 'chatbot-enlarged': isEnlarged }]"
      >
        <div class="chatbot-header">
          <h3 class="chatbot-title">AI Assistant</h3>
          <div class="chatbot-controls">
            <button 
              @click="toggleEnlarge"
              class="chatbot-control-btn"
              :aria-label="isEnlarged ? 'Minimize' : 'Enlarge'"
            >
              <svg 
                v-if="!isEnlarged"
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                class="control-icon"
              >
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
              </svg>
              <svg 
                v-else
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                class="control-icon"
              >
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
              </svg>
            </button>
            <button 
              @click="closeChat"
              class="chatbot-control-btn chatbot-close-btn"
              aria-label="Close"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                class="control-icon"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="chatbot-content">
          <iframe 
            :src="chatbotUrl"
            class="chatbot-iframe"
            frameborder="0"
            allow="microphone; camera"
            title="AI Chatbot"
          ></iframe>
        </div>
      </div>
    </transition>

    <!-- Backdrop for enlarged view -->
    <transition name="fade">
      <div 
        v-if="isOpen && isEnlarged"
        class="chatbot-backdrop"
        @click="toggleEnlarge"
      ></div>
    </transition>
  </div>
</template>

<script>
import GeneralVariables from '../../utils/GeneralVariables';

export default {
  name: 'ChatbotButton',
  data() {
    return {
      isOpen: false,
      isEnlarged: false,
      chatbotUrl: GeneralVariables.CHATBOT_URL
    };
  },
  methods: {
    openChat() {
      this.isOpen = true;
    },
    closeChat() {
      this.isOpen = false;
      this.isEnlarged = false;
    },
    toggleEnlarge() {
      this.isEnlarged = !this.isEnlarged;
    }
  }
};
</script>

<style scoped lang="scss">
.chatbot-wrapper {
  position: fixed;
  z-index: 9999;
}

.chatbot-floating-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--app-primary-color, #007bff);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  z-index: 9999;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  @media screen and (max-width: 768px) {
    width: 50px;
    height: 50px;
    bottom: 15px;
    right: 15px;
  }
}

.chatbot-icon {
  width: 32px;
  height: 32px;

  @media screen and (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
}

.chatbot-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 400px;
  height: 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
  z-index: 10000;

  @media screen and (max-width: 768px) {
    width: calc(100vw - 30px);
    height: calc(100vh - 100px);
    bottom: 15px;
    right: 15px;
  }

  &.chatbot-enlarged {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    height: 90vh;
    max-width: 1200px;
    max-height: 900px;
    bottom: auto;
    right: auto;
    z-index: 10001;

    @media screen and (max-width: 768px) {
      width: 95vw;
      height: 95vh;
    }
  }
}

.chatbot-header {
  background: var(--app-primary-color, #007bff);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.chatbot-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
}

.chatbot-controls {
  display: flex;
  gap: 8px;
}

.chatbot-control-btn {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
}

.control-icon {
  width: 20px;
  height: 20px;
}

.chatbot-content {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.chatbot-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.chatbot-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
}

/* Animations */
.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s ease;
}

.chat-slide-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.chat-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
